import { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ClientSignatureField from "../components/ClientSignatureField";
import { signatureProblem, signaturePayload } from "../lib/clientSignature";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { X, Plus, Users, AlertCircle } from "lucide-react";
// Firm identity from the tenant fixture (PRD_PHASE_1A_FIRM.md §3.2.1).
import { FIRM } from "../fixtures/firmIdentity";
import axios from "axios";
import { TOB_SECTIONS, TOB_VERSION } from "./onboarding/tobContent";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { LOGO_URL, KOI_BG_URL } from "../config/constants";

// BM-H3490: residential matter types. At OB time the form only knows the
// practice area ("01 RESI", from the conflict check); the specific codes are
// listed for links made after the FORF. Vanessa, 3 Oct 2026: "only resi".
const RESI_MATTER_TYPES = ["01 RESI", "purchase_resi", "sale_resi", "remortgage", "remortgage_toe", "toe_only", "auction_review", "declaration_trust", "restrictive_covenant", "licence_to_assign", "licence_alterations", "title_split"];

// BM-H3668: SDLT is also filed on commercial property purchases and leases
// (Sangita, via Vanessa, 5 Oct 2026), so the SDLT tax reference questions
// (NI number, CoHo auth code) show there too. "03 Commercial Property" is
// the practice area the form gets from the conflict check at OB time.
const COMMERCIAL_PROPERTY_MATTER_TYPES = ["03 Commercial Property", "comm_freehold_purchase", "comm_freehold_sale", "comm_lease_landlord", "comm_lease_tenant", "comm_lease_assignment", "comm_lease_surrender", "comm_lease_assgn_surr"];
const SDLT_MATTER_TYPES = [...RESI_MATTER_TYPES, ...COMMERCIAL_PROPERTY_MATTER_TYPES];

// Word-boundary suffix pattern that strongly implies a registered company /
// LLP / PLC / CIC. Mirrors backend/hub/onboarding.py COMPANY_NAME_PATTERN.
// Server-side check in /api/onboarding/submit is authoritative; this is UX
// only — it surfaces a clear inline error before the POST so the client
// does not see a 422 toast.
const COMPANY_NAME_PATTERN = /\b(ltd|limited|llp|llc|plc|cic|cio|gmbh|sa|nv)\b/i;

// Canonical matter reference shape: BL.<practice>.<feeearner>.<seq>.
// Used to decide whether a URL ref param should be sent as
// matter_reference (canonical) vs provisional_id (legacy OB-YYYY-NNNN).
const CANONICAL_MATTER_REF_PATTERN = /^BL\./i;

// BM-H3851: the Letter of Authority scope boxes, (a) to (e), same codes as
// backend/hub/authorised_instructors.py SCOPE_LABELS.
const AUTHORISED_SCOPE_OPTIONS = [
  ["instructions", "Give us instructions on all aspects of the matter"],
  ["advice", "Receive our advice, letters and copies of documents"],
  ["approve_documents", "Approve the terms of documents (this does not let them sign anything for you)"],
  ["agree_terms", "Agree settlement, completion or other terms that bind you"],
  ["payment_instructions", "Give us payment instructions, including bank details"],
];

const MATTER_TYPE_OPTIONS = [
  { value: "purchase_resi", label: "Residential Purchase" },
  { value: "sale_resi", label: "Residential Sale" },
  { value: "remortgage", label: "Remortgage" },
  { value: "transfer_equity", label: "Transfer of Equity" },
  { value: "commercial_property", label: "Commercial Property" },
  { value: "commercial_contract", label: "Commercial Contract / Agreement" },
  { value: "business_sale_purchase", label: "Business Sale / Purchase" },
  { value: "shareholder_agreement", label: "Shareholder / Partnership Agreement" },
  { value: "employment", label: "Employment" },
  { value: "dispute_litigation", label: "Dispute / Litigation" },
  { value: "debt_recovery", label: "Debt Recovery" },
  { value: "other", label: "Other / Not sure" },
];

const koiStyle = {
  position: "fixed",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  opacity: 0.18,
  zIndex: 0,
  pointerEvents: "none",
  userSelect: "none",
};

export default function ClientOnboarding() {
  const [searchParams] = useSearchParams();
  // Email link contract (set in backend/hub/onboarding.py:send_onboarding_from_conflict_check):
  //   /client-onboarding?ref=<canonical_ref>&t=<link_token>
  // Legacy email links use ?pid=<provisional_id>. Both must be honoured.
  // If neither is present, the link is invalid — see hasValidIdentifier
  // below for the explicit "contact your fee earner" gate.
  const ref = searchParams.get("ref") || "";
  const pid = searchParams.get("pid") || "";
  const linkToken = searchParams.get("t") || "";
  // BM-H469: a corporate client whose onboarding was linked or skipped
  // accepts the terms on the terms page, then arrives here for the
  // Schedule 1 guarantee. The guarantee step is not rebuilt over there;
  // the client is sent to this one.
  const openAtGuarantee = (searchParams.get("step") || "") === "guarantee";
  // refOrPid: whichever identifier is present. Used for prefill lookups
  // and to choose the correct submit-body field below.
  const refOrPid = ref || pid;
  // Valid link = either legacy (pid) OR new format (ref+t). A bare ?ref=
  // without a token is rejected — that combination would bypass the R4
  // link_token binding added in PR #343 and silently land in an orphan.
  const hasValidIdentifier = Boolean(pid) || (Boolean(ref) && Boolean(linkToken));
  const [step, setStep] = useState("tob");
  // ── Director's guarantee (ToB clause 1.5 + Schedule 1, Sep 2026) ──
  // A body corporate's acceptance of the Terms is NOT the guarantor's
  // signature. The guarantee is therefore its OWN step, reached only
  // AFTER the company's submission has been accepted, with its own
  // confirmations and its own typed signature posted to its own
  // endpoint (POST /api/onboarding/director-guarantee). It must never
  // be completable by the same action that accepts the Terms for the
  // company — that separation is the legal point (s4 Statute of
  // Frauds 1677), not a UI preference.
  const [guaranteeSchedule, setGuaranteeSchedule] = useState([]);
  const [guaranteeScheduleError, setGuaranteeScheduleError] = useState("");
  const [guaranteeRead, setGuaranteeRead] = useState(false);
  const [guaranteeData, setGuaranteeData] = useState({
    guarantor_name: "", position: "", home_address: "", email: "",
    typed_signature: "",
  });
  const [guaranteePersonal, setGuaranteePersonal] = useState(false);
  const [guaranteeAdvice, setGuaranteeAdvice] = useState(false);
  const [guaranteeSubmitting, setGuaranteeSubmitting] = useState(false);
  const [guaranteeError, setGuaranteeError] = useState("");
  const [guaranteeDone, setGuaranteeDone] = useState(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [tobAccepted, setTobAccepted] = useState(false);
  const [tobAcceptedAt, setTobAcceptedAt] = useState(null);
  const [entityType, setEntityType] = useState("");
  const [entityTypeError, setEntityTypeError] = useState(false);
  const [matterType, setMatterType] = useState("");
  const [formData, setFormData] = useState({
    full_name: "", email: "", phone: "", address: "",
    company_name: "", company_registration_number: "", company_registered_office: "",
    utr_or_vat: "", utr_vat_type: "VAT",
    // BM-H500 (Vanessa, via Jodie/Rebekah on Teams): the CoHo auth code
    // is needed for the company's own property purchase (SDLT filing at
    // Companies House-linked transactions), not for every corporate
    // client regardless of matter type — same purchase-only reasoning
    // as national_insurance_number below.
    companies_house_auth_code: "",
    is_director: "", capacity: "", matter_description: "",
    date_of_birth: "", nationality: "", national_insurance_number: "",
    occupation: "",
    acting_for_self: "",
    // PR O (21May2026, Vanessa): capture conditional free-text when the
    // client says they are NOT acting in their own name. Previously the
    // OB form let them select "No" and silently move on, leaving admin
    // to chase the basis of agency by email. Now required when
    // acting_for_self === "no" so the FORF surfaces a Capacity note
    // for the fee earner without manual chase.
    acting_for_self_details: "",
    // Backend (PrimaryOnboardingSubmission) expects
    //   beneficial_owners: Optional[List[BeneficialOwner]] = []
    // where BeneficialOwner = { name, percentage?, address? }.
    // It MUST initialise as an array — prior to 20 May 2026 this was
    // an empty string bound to a free-text textarea, which made every
    // submission (including individual + sole-BO cases) trip pydantic
    // with "Input should be a valid list".
    beneficial_owners: [],
    // BM-H3851 (Vanessa, 7 Oct 2026): will anyone other than the client give
    // us instructions? On a yes we take each person's details so ClientCare
    // can send them a Thirdfort ID link (MLR 2017 reg 28(10)) and the Letter
    // of Authority can be prepared. Rows: { full_name, role, email, mobile,
    // date_of_birth, scope: [codes], limits }.
    others_instructing: "",
    authorised_instructors: [],
    // Companion flag for company/LLP submissions where the entity has
    // no individual PSCs (e.g. AIM-listed, fully diffuse holdings).
    // When true, the form posts beneficial_owners: [] and the validator
    // is satisfied without requiring at least one named owner.
    no_individual_pscs: false,
    acting_for_entity: "",
  });
  const [estateAgentAuthority, setEstateAgentAuthority] = useState("");
  // BM-H, 21 Aug 2026 (Vanessa): resi-only will-status question. No
  // upsell on this screen — client is mid-conveyancing-form, not
  // shopping for a will. If they say no/not sure we just tell them the
  // wills guide will ride along with their onboarding answers copy;
  // the actual attach happens server-side in send_onboarding_confirmation_email.
  const [hasWill, setHasWill] = useState("");
  const [estateAgentIdDisclosure, setEstateAgentIdDisclosure] = useState("");
  const [estateAgentDetailLimit, setEstateAgentDetailLimit] = useState("");
  const [estateAgentNames, setEstateAgentNames] = useState("");
  const [jointClients, setJointClients] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [result, setResult] = useState(null);
  const sigCanvasRef = useRef(null);
  // BM-H588: sign by drawing or by typing the full name.
  const [sigMode, setSigMode] = useState("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const tobContainerRef = useRef(null);

  // BM-H3498: was purchase/sale only, which the form never learned, so this
  // section (and the has-a-will question it carries) never showed.
  const showEstateAgentSection = RESI_MATTER_TYPES.includes(matterType);
  const showNiNumber = SDLT_MATTER_TYPES.includes(matterType) && entityType === "individual";
  // BM-H500 gated these on "purchase_resi", which the form never learned,
  // so they never showed (BM-H3490). Purchase vs sale is not known at OB
  // time, so both now show on any residential matter. The UTR/VAT field
  // keeps its own, broader gate below.
  const showCompaniesHouseAuthCode =
    SDLT_MATTER_TYPES.includes(matterType) && ["company", "llp"].includes(entityType);
  // Hide the matter-type fallback selector whenever any identifier is
  // present (prefill will populate matter_type from the OB stub). Without
  // an identifier the user is on the invalid-link screen anyway.
  const showMatterTypeSelect = !refOrPid && !matterType;

  useEffect(() => {
    if (!openAtGuarantee || !linkToken) return;
    (async () => {
      try {
        const sched = await axios.get(`${API}/onboarding/director-guarantee/${encodeURIComponent(linkToken)}/schedule`);
        setGuaranteeSchedule(sched.data?.sections || []);
      } catch {
        setGuaranteeScheduleError(
          `The guarantee schedule could not be loaded. Please contact ${FIRM.clientCareEmail}.`
        );
      }
      setStep("guarantee");
    })();
  }, [openAtGuarantee, linkToken]);

  useEffect(() => {
    // Prefill on either ref or pid. The backend prefill endpoint already
    // accepts both forms (see backend/hub/onboarding.py:get_onboarding_prefill).
    if (!refOrPid) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/onboarding/prefill/${encodeURIComponent(refOrPid)}`);
        if (res.data.matter_type) setMatterType(res.data.matter_type);
        if (res.data.client_name) setFormData(prev => ({ ...prev, full_name: res.data.client_name }));
        if (res.data.client_email) setFormData(prev => ({ ...prev, email: res.data.client_email }));
        if (res.data.matter_description) setFormData(prev => ({ ...prev, matter_description: res.data.matter_description }));
      } catch { /* prefill is optional */ }
    })();
  }, [refOrPid]);

  const handleScroll = useCallback(() => {
    const el = tobContainerRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) setHasScrolledToBottom(true);
  }, []);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleTobAccepted = (checked) => {
    setTobAccepted(checked);
    if (checked && !tobAcceptedAt) {
      setTobAcceptedAt(new Date().toISOString());
    } else if (!checked) {
      setTobAcceptedAt(null);
    }
  };

  const handleEntityTypeSelect = (t) => {
    setEntityType(t);
    setEntityTypeError(false);
  };

  const addJointClient = () => setJointClients(prev => [...prev, { name: "", email: "", mobile: "" }]);
  const removeJointClient = (i) => setJointClients(prev => prev.filter((_, idx) => idx !== i));
  const updateJointClient = (i, field, value) => {
    setJointClients(prev => prev.map((jc, idx) => idx === i ? { ...jc, [field]: value } : jc));
  };

  // ── Beneficial owner row helpers ───────────────────────────────────
  // Mirrors the Joint Clients UX (add/remove/update rows). Each row
  // maps 1:1 onto a backend `BeneficialOwner` { name, percentage }.
  // Address is intentionally NOT collected here — the schema accepts
  // it but we don't ask the client for it on the OB form; addresses
  // are gathered via the Thirdfort ID check that follows.
  const addBeneficialOwner = () =>
    setFormData(prev => ({
      ...prev,
      beneficial_owners: [...prev.beneficial_owners, { name: "", percentage: "" }],
      // Adding a row implicitly negates the "no individual PSCs" toggle
      // so the two states can never disagree.
      no_individual_pscs: false,
    }));
  const removeBeneficialOwner = (i) =>
    setFormData(prev => ({
      ...prev,
      beneficial_owners: prev.beneficial_owners.filter((_, idx) => idx !== i),
    }));
  const updateBeneficialOwner = (i, field, value) =>
    setFormData(prev => ({
      ...prev,
      beneficial_owners: prev.beneficial_owners.map(
        (bo, idx) => idx === i ? { ...bo, [field]: value } : bo
      ),
    }));
  const toggleNoIndividualPscs = (checked) =>
    setFormData(prev => ({
      ...prev,
      no_individual_pscs: checked,
      // When the user ticks "No individual PSCs" any half-filled rows
      // are cleared so the submission posts a clean empty list. The
      // toggle is the source of truth in that mode.
      beneficial_owners: checked ? [] : prev.beneficial_owners,
    }));

  // ── BM-H3851: authorised people row helpers ─────────────────────────
  const emptyAuthorisedPerson = () => ({
    full_name: "", role: "", email: "", mobile: "", date_of_birth: "",
    scope: ["instructions", "advice"], limits: "",
  });
  const setOthersInstructing = (v) =>
    setFormData(prev => ({
      ...prev,
      others_instructing: v,
      authorised_instructors: v === "yes"
        ? (prev.authorised_instructors.length ? prev.authorised_instructors : [emptyAuthorisedPerson()])
        : [],
    }));
  const addAuthorisedPerson = () =>
    setFormData(prev => ({
      ...prev,
      authorised_instructors: [...prev.authorised_instructors, emptyAuthorisedPerson()],
    }));
  const removeAuthorisedPerson = (i) =>
    setFormData(prev => ({
      ...prev,
      authorised_instructors: prev.authorised_instructors.filter((_, idx) => idx !== i),
    }));
  const updateAuthorisedPerson = (i, field, value) =>
    setFormData(prev => ({
      ...prev,
      authorised_instructors: prev.authorised_instructors.map(
        (p, idx) => idx === i ? { ...p, [field]: value } : p
      ),
    }));
  const toggleAuthorisedScope = (i, code, checked) =>
    setFormData(prev => ({
      ...prev,
      authorised_instructors: prev.authorised_instructors.map((p, idx) => {
        if (idx !== i) return p;
        const scope = (p.scope || []).filter(c => c !== code);
        return { ...p, scope: checked ? [...scope, code] : scope };
      }),
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = [];

    if (!entityType) {
      setEntityTypeError(true);
      missing.push("Client type (Individual / Company / LLP)");
    }

    if (entityType === "other") {
      setErrors(["We cannot process this submission automatically for this entity type. Please contact clientcare@bonsai.law so we can assist you directly."]);
      return;
    }

    if (!formData.full_name) missing.push("Full name");
    if (!formData.email) missing.push("Email address");
    if (!formData.phone) missing.push("Phone number");
    if (!formData.address) missing.push("Address");

    if (["company", "llp"].includes(entityType)) {
      const label = entityType === "llp" ? "LLP" : "Company";
      if (!formData.company_name) missing.push(`${label} name`);
      if (!formData.company_registration_number) missing.push(`${label} registration number`);
      if (!(formData.company_registered_office || "").trim()) missing.push(`${label} registered office address`);
      if (!formData.is_director) missing.push(`Confirm whether you are a director/member of the ${label}`);
      if (!formData.capacity) missing.push("Your capacity");
      if (!formData.acting_for_entity) missing.push(`Confirm authorisation to act on behalf of the ${label}`);
      // Either at least one named BO OR the explicit "no individual PSCs"
      // disclosure must be present — silent zero-row submissions would
      // satisfy the pydantic schema but breach our AML obligations.
      const hasNamedBo = formData.beneficial_owners.some(bo => (bo.name || "").trim());
      if (!formData.no_individual_pscs && !hasNamedBo) {
        missing.push(
          `At least one beneficial owner / PSC of the ${label} ` +
          `(or tick "No individual PSCs" if the ${label} has none)`
        );
      }
    }

    if (entityType === "individual") {
      if (!formData.date_of_birth) missing.push("Date of birth");
      if (!formData.nationality) missing.push("Nationality");
      if (!formData.occupation) missing.push("Occupation");
      if (!formData.acting_for_self) missing.push("Confirm whether you are acting in your own name");
      // PR O: if acting on behalf of someone else, require a brief explanation
      // so admin can pick up the basis of agency on the FORF without chasing.
      if (formData.acting_for_self === "no" && (formData.acting_for_self_details || "").trim().length < 5) {
        missing.push("Brief details of whose behalf you are acting on");
      }
    }

    // BM-H3851: asked of every client, and the details Thirdfort needs are
    // required for each person named.
    if (!formData.others_instructing) {
      missing.push("Tell us whether anyone else will give us instructions on this matter");
    }
    if (formData.others_instructing === "yes") {
      const people = formData.authorised_instructors;
      if (!people.length) missing.push("Details of the person who will give us instructions");
      people.forEach((p, i) => {
        const who = (p.full_name || "").trim() || `Person ${i + 1}`;
        if (!(p.full_name || "").trim()) missing.push(`Person ${i + 1}: full name`);
        if (!(p.role || "").trim()) missing.push(`${who}: role or relationship`);
        if (!(p.email || "").trim()) missing.push(`${who}: email address`);
        if (!(p.mobile || "").trim()) missing.push(`${who}: mobile number`);
        if (!p.date_of_birth) missing.push(`${who}: date of birth`);
        if (!(p.scope || []).length) missing.push(`${who}: what they may instruct us on`);
      });
    }

    if (!formData.matter_description || formData.matter_description.trim().length < 20) {
      missing.push('Matter description (minimum 20 characters)');
    }

    if (!tobAccepted) missing.push("Agreement to Terms of Business");
    {
      const sigIssue = signatureProblem({
        mode: sigMode,
        typedName: typedSignature,
        fullName: formData.full_name,
        canvasEmpty: sigMode === "draw" ? (sigCanvasRef.current ? sigCanvasRef.current.isEmpty() : true) : false,
      });
      if (sigIssue) missing.push(sigIssue);
    }

    for (const jc of jointClients) {
      if (!jc.name || !jc.email) {
        missing.push("Complete all joint client details (name and email)");
        break;
      }
    }
    // Brief 37: if a joint-client mobile is entered, validate it loosely as a UK number.
    // Empty mobile is allowed (admin can chase before Thirdfort dispatch).
    for (const jc of jointClients) {
      if (jc.mobile && !/^(\+?44|0)\s?7\d{3}\s?\d{6}$/.test(jc.mobile.replace(/\s+/g, ""))) {
        missing.push("Joint client mobile must be a valid UK mobile number (or left blank)");
        break;
      }
    }

    if (missing.length > 0) {
      setErrors(missing);
      return;
    }

    // Entity-type vs name consistency check (mirrors backend hard-stop).
    // See backend/hub/onboarding.py:_looks_like_company_name. Server is
    // authoritative — this is purely to give the user inline feedback
    // before the POST.
    if (entityType === "individual") {
      const nameLooksLikeCompany = COMPANY_NAME_PATTERN.test(formData.full_name || "");
      const explicitCompanyName = !!(formData.company_name && formData.company_name.trim());
      if (nameLooksLikeCompany || explicitCompanyName) {
        setErrors([
          "The name you entered looks like a registered company. " +
          "Please change the client type to Company or LLP, or correct the name. " +
          "Contact clientcare@bonsai.law if you believe this is correct.",
        ]);
        return;
      }
    }

    setErrors([]);
    setSubmitting(true);
    try {
      // Drop the UI-only `no_individual_pscs` flag before posting — the
      // backend schema doesn't know about it and would reject on
      // model_config.extra="forbid" if that ever flips on. The shape
      // it cares about is just `beneficial_owners: [...]`.
      // Also normalise individuals + sole-BO + no-PSC cases to an empty
      // list so a stricter pydantic version cannot regress this.
      const { no_individual_pscs: _noPscs, ...formForPayload } = formData;
      const beneficialOwnersForPayload = ["company", "llp"].includes(entityType)
        ? (formData.no_individual_pscs
            ? []
            : formData.beneficial_owners.filter(bo => (bo.name || "").trim()))
        : [];
      const payload = {
        entity_type: entityType,
        ...formForPayload,
        beneficial_owners: beneficialOwnersForPayload,
        // BM-H3851: rows only travel on a yes.
        authorised_instructors: formData.others_instructing === "yes"
          ? formData.authorised_instructors.filter(p => (p.full_name || "").trim())
          : [],
        matter_type: matterType || "",
        signature: signaturePayload({ mode: sigMode, typedName: typedSignature, canvasRef: sigCanvasRef }),
        agreed_tob: true,
        tob_version: TOB_VERSION, // BM-H3379
        tob_accepted_at: tobAcceptedAt || new Date().toISOString(),
        joint_clients: jointClients,
      };
      // Forward all three correlation fields the backend recognises.
      // Either provisional_id (legacy OB-YYYY-NNNN) OR matter_reference (BL.X...)
      // must be present, plus the link_token when supplied — without these,
      // the backend will reject the submission with ob_submit_no_correlation
      // (see backend/hub/onboarding.py:submit_primary_onboarding). This
      // closes the silent orphan-mint regression introduced in PR #343
      // — diagnostic at /home/user/workspace/ob-return-pipeline-advance-diagnostic-30Apr2026.md.
      if (refOrPid) {
        if (CANONICAL_MATTER_REF_PATTERN.test(refOrPid)) {
          payload.matter_reference = refOrPid;
        } else {
          payload.provisional_id = refOrPid;
        }
      }
      if (linkToken) payload.link_token = linkToken;
      if (showEstateAgentSection) {
        payload.estate_agent_authority = estateAgentAuthority || null;
        payload.estate_agent_id_disclosure = estateAgentAuthority === "yes" ? (estateAgentIdDisclosure || null) : null;
        payload.estate_agent_detail_limit = estateAgentDetailLimit || null;
        payload.estate_agent_names = estateAgentAuthority === "yes" ? (estateAgentNames || null) : null;
        payload.has_will = hasWill || null;
      }
      const res = await axios.post(`${API}/onboarding/submit`, payload);
      setResult(res.data);
      // Corporate clients: the company has now accepted the Terms. The
      // guarantee is a SEPARATE, subsequent step — never bundled into
      // the acceptance above. Without a link token we cannot bind the
      // guarantee to the matter, so the firm follows up out of band.
      if (["company", "llp"].includes(entityType) && linkToken) {
        try {
          const sched = await axios.get(`${API}/onboarding/director-guarantee/${encodeURIComponent(linkToken)}/schedule`);
          setGuaranteeSchedule(sched.data?.sections || []);
        } catch (schedErr) {
          // Never present a guarantee we cannot reproduce in full.
          setGuaranteeScheduleError(
            `The guarantee schedule could not be loaded. Please contact ${FIRM.clientCareEmail}.`
          );
        }
        setStep("guarantee");
      } else {
        setStep("success");
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;

      // 4xx — actionable validation / correlation errors. Surface message verbatim.
      if (status && status >= 400 && status < 500) {
        const errorCode = (detail && typeof detail === "object") ? detail.error : null;
        // ── Token-required / correlation-failure surfacing ─────────────
        // After the 01 May 2026 hardening (PR "OB submit: hard-reject
        // tokenless/invalid-token correlation") the backend rejects with
        // explicit error codes when the link token is missing, unknown,
        // or mismatched. We MUST NOT show the false-success toast in this
        // state — diagnostic at
        // /home/user/workspace/pipeline-email-audit-30Apr2026.md (bug #2).
        // Instead the user is routed to a dedicated step rendering a
        // remediation message asking them to request a fresh link.
        if (errorCode === "ob_submit_token_invalid" || errorCode === "ob_submit_no_correlation") {
          setResult({ correlation_error: errorCode, message: detail.message || "" });
          setStep("link_invalid");
          return;
        }
        let message = "Submission failed. Please try again or contact clientcare@bonsai.law";
        if (typeof detail === "string") {
          message = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          // Pydantic v2 422: detail is [{loc, msg, type, ...}] — extract field-level messages.
          const fields = detail.map(d => {
            const field = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : 'field';
            return `${field}: ${d.msg}`;
          }).join('; ');
          message = fields || 'Validation failed';
        } else if (detail && typeof detail === "object" && detail.message) {
          message = detail.message;
        }
        // Standing rule: route through window.errorMessage so the real backend error is
        // always visible and never silenced (see window.errorMessage convention, PR #498).
        if (typeof window !== "undefined" && typeof window.errorMessage === "function") {
          try {
            window.errorMessage(err, message);
          } catch (_) {
            setErrors([message]);
          }
        } else {
          setErrors([message]);
        }
        return;
      }

      // 5xx — server-side, never surface raw detail.message; show a generic apology.
      // Detail message is also sanitised at backend post-28-May-2026, so this is
      // defence-in-depth. See client_portal_ob_error_incident_28May2026.md.
      console.error("OB submit server error", { status, detail, err });
      const generic = (detail && typeof detail === "object" && typeof detail.message === "string")
        ? detail.message
        : "Something went wrong on our side. Your details have been recorded and our team has been notified. Please contact us at clientcare@bonsai.law if you do not hear from us within one business day.";
      setErrors([generic]);
    } finally {
      setSubmitting(false);
    }
  };

  // Invalid-link gate. We render this BEFORE the success branch so a stale
  // tab cannot land on success. Triggered when neither pid nor (ref+t) is
  // present in the URL — the email link contract requires one of these.
  // Trust-context: explicit error with a clear remediation, NOT a blank
  // form that silently submits to an orphan record.
  if (!hasValidIdentifier) {
    return (
      <div
        className="relative min-h-screen bg-navy flex items-center justify-center p-4"
        data-testid="onboarding-invalid-link"
      >
        <img src={KOI_BG_URL} alt="" aria-hidden="true" style={koiStyle} />
        <div className="relative z-10 max-w-lg w-full text-center">
          <img src={LOGO_URL} alt="Bonsai Law" className="h-16 mx-auto mb-8" />
          <div className="glass-card p-8">
            <div className="w-16 h-16 bg-status-red-dim/40 rounded-md flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-status-red" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Invalid onboarding link</h2>
            <p className="text-slate-400 text-sm mb-4">
              We could not identify your matter from this link. Please use the
              link from your onboarding email, or contact your fee earner so
              they can resend it.
            </p>
            <p className="text-slate-400 text-xs mt-4">
              Questions? Contact{" "}
              <a href="mailto:clientcare@bonsai.law" className="text-teal hover:underline">
                clientcare@bonsai.law
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Link-invalid / token-required state. Rendered when the backend
  // rejects with ob_submit_token_invalid or ob_submit_no_correlation.
  // CRITICAL: this branch MUST be reachable WITHOUT mutating state into
  // ``success`` — surfacing a false-success toast on a rejected
  // submission is exactly the bug we are closing
  // (/home/user/workspace/pipeline-email-audit-30Apr2026.md bug #2).
  if (step === "link_invalid") {
    const errorCode = result?.correlation_error || "ob_submit_token_invalid";
    return (
      <div
        className="relative min-h-screen bg-navy flex items-center justify-center p-4"
        data-testid="onboarding-link-invalid"
        data-correlation-error={errorCode}
      >
        <img src={KOI_BG_URL} alt="" aria-hidden="true" style={koiStyle} />
        <div className="relative z-10 max-w-lg w-full text-center">
          <img src={LOGO_URL} alt="Bonsai Law" className="h-16 mx-auto mb-8" />
          <div className="glass-card p-8">
            <div className="w-16 h-16 bg-status-red-dim/40 rounded-md flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-status-red" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              We could not submit this onboarding form
            </h2>
            <p className="text-slate-300 text-sm mb-4">
              {result?.message ||
                "Your onboarding link could not be verified. Please request a fresh link from your fee earner."}
            </p>
            <p className="text-slate-400 text-sm mb-2">
              Please contact your fee earner at Bonsai Law (or
              {" "}
              <a href="mailto:clientcare@bonsai.law" className="text-teal hover:underline">
                clientcare@bonsai.law
              </a>
              ) and ask them to re-send your onboarding email so you can
              complete this form.
            </p>
            <p className="text-slate-500 text-xs mt-6 font-mono">
              Reference code: {errorCode}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === "guarantee") {
    const guaranteeReady = (
      guaranteeRead && guaranteePersonal && guaranteeAdvice
      && guaranteeData.guarantor_name.trim().length > 1
      && guaranteeData.position.trim().length > 1
      && guaranteeData.home_address.trim().length > 7
      && guaranteeData.email.trim().length > 4
      && guaranteeData.typed_signature.trim().length > 1
      && !guaranteeScheduleError
    );

    const submitGuarantee = async () => {
      setGuaranteeError("");
      setGuaranteeSubmitting(true);
      try {
        const res = await axios.post(`${API}/onboarding/director-guarantee`, {
          link_token: linkToken,
          matter_reference: CANONICAL_MATTER_REF_PATTERN.test(refOrPid) ? refOrPid : null,
          guarantor_name: guaranteeData.guarantor_name.trim(),
          position: guaranteeData.position.trim(),
          home_address: guaranteeData.home_address.trim(),
          email: guaranteeData.email.trim(),
          personal_capacity_confirmed: guaranteePersonal,
          independent_advice_acknowledged: guaranteeAdvice,
          typed_signature: guaranteeData.typed_signature.trim(),
          signature_method: "typed_electronic",
          tob_version: TOB_VERSION, // BM-H3379
          signed_at: new Date().toISOString(),
        });
        setGuaranteeDone(res.data);
        setStep("success");
      } catch (err) {
        setGuaranteeError(
          errorMessage(err, `The guarantee could not be recorded. Please contact ${FIRM.clientCareEmail}.`)
        );
      } finally {
        setGuaranteeSubmitting(false);
      }
    };

    return (
      <div className="relative min-h-screen bg-navy p-4" data-testid="onboarding-guarantee">
        <img src={KOI_BG_URL} alt="" aria-hidden="true" style={koiStyle} />
        <div className="relative z-10 max-w-3xl mx-auto py-8">
          <img src={LOGO_URL} alt={FIRM.tradingName} className="h-14 mx-auto mb-6" />
          <div className="glass-card p-6">
            <h2 className="text-xl font-bold text-white mb-2">
              Step 2 of 2: Guarantee by a director, member or designated member
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              The company's Terms of Business have been accepted and recorded.
              This second step is separate and personal to you. You are being
              asked to guarantee the company's fees in your own name, so it
              must be signed by you personally and not on the company's behalf.
            </p>
            {guaranteeScheduleError ? (
              <p className="text-status-red text-sm mb-4" data-testid="guarantee-schedule-error">
                {guaranteeScheduleError}
              </p>
            ) : (
              <div
                className="max-h-80 overflow-y-auto bg-navy-card border border-navy-border rounded-md p-4 mb-4 text-sm text-slate-300 whitespace-pre-wrap"
                data-testid="guarantee-schedule"
              >
                {guaranteeSchedule.map((sec, i) => (
                  <div key={i} className="mb-4">
                    <p className="font-semibold text-txt mb-2">{sec.title}</p>
                    <p>{sec.content}</p>
                  </div>
                ))}
              </div>
            )}

            <label className="flex items-start gap-2 mb-3 text-sm text-slate-300">
              <input type="checkbox" checked={guaranteeRead}
                onChange={(e) => setGuaranteeRead(e.target.checked)}
                data-testid="guarantee-read" className="mt-1" />
              <span>I have read Schedule 1 in full.</span>
            </label>
            <label className="flex items-start gap-2 mb-3 text-sm text-slate-300">
              <input type="checkbox" checked={guaranteePersonal}
                onChange={(e) => setGuaranteePersonal(e.target.checked)}
                data-testid="guarantee-personal" className="mt-1" />
              <span>
                I am giving this guarantee in my own personal capacity and I
                understand I may be required to pay the company's fees myself.
              </span>
            </label>
            <label className="flex items-start gap-2 mb-4 text-sm text-slate-300">
              <input type="checkbox" checked={guaranteeAdvice}
                onChange={(e) => setGuaranteeAdvice(e.target.checked)}
                data-testid="guarantee-advice" className="mt-1" />
              <span>
                I acknowledge that {FIRM.tradingName} has recommended I take
                independent legal advice before giving this guarantee, and
                that {FIRM.tradingName} does not advise me on it.
              </span>
            </label>

            <div className="grid gap-3 sm:grid-cols-2 mb-4">
              {[
                ["guarantor_name", "Your full name", "text"],
                ["position", "Position held (director, member, designated member)", "text"],
                ["email", "Your email address", "email"],
                ["home_address", "Your home address", "text"],
              ].map(([field, label, type]) => (
                <div key={field} className={field === "home_address" ? "sm:col-span-2" : ""}>
                  <label className="block text-xs text-slate-400 mb-1">{label}</label>
                  <input
                    type={type}
                    value={guaranteeData[field]}
                    onChange={(e) => setGuaranteeData({ ...guaranteeData, [field]: e.target.value })}
                    data-testid={`guarantee-${field.replace(/_/g, "-")}`}
                    className="w-full h-11 px-3 rounded-md bg-navy-card border border-navy-border text-txt text-sm"
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-400 mb-1">
                  Type your full name to sign this guarantee personally
                </label>
                <input
                  type="text"
                  value={guaranteeData.typed_signature}
                  onChange={(e) => setGuaranteeData({ ...guaranteeData, typed_signature: e.target.value })}
                  data-testid="guarantee-signature"
                  className="w-full h-11 px-3 rounded-md bg-navy-card border border-navy-border text-txt text-sm"
                />
              </div>
            </div>

            {guaranteeError && (
              <p className="text-status-red text-sm mb-3" data-testid="guarantee-error">{guaranteeError}</p>
            )}

            <Button
              type="button"
              onClick={submitGuarantee}
              disabled={!guaranteeReady || guaranteeSubmitting}
              data-testid="guarantee-submit"
              className="h-11 w-full"
            >
              {guaranteeSubmitting ? "Recording guarantee..." : "Sign the guarantee"}
            </Button>
            <p className="text-xs text-slate-500 mt-3">
              If you are not able to give this guarantee, please contact
              {FIRM.clientCareEmail} before the matter proceeds.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="relative min-h-screen bg-navy flex items-center justify-center p-4" data-testid="onboarding-success">
        <img src={KOI_BG_URL} alt="" aria-hidden="true" style={koiStyle} />
        <div className="relative z-10 max-w-lg w-full text-center">
          <img src={LOGO_URL} alt="Bonsai Law" className="h-16 mx-auto mb-8" />
          <div className="glass-card p-8">
            <div className="w-16 h-16 bg-status-green-dim/40 rounded-md flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-status-green" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Onboarding Complete</h2>
            <p className="text-slate-400 text-sm mb-4">
              Thank you for completing the onboarding form and agreeing to our Terms of Business.
            </p>
            {guaranteeDone?.guarantor_name && (
              <p className="text-slate-400 text-sm mb-4" data-testid="guarantee-confirmed">
                Your personal guarantee under Schedule 1 has been recorded in the
                name of {guaranteeDone.guarantor_name}. A copy is held on the matter file.
              </p>
            )}
            {result?.provisional_id && (
              <p className="text-xs text-slate-400 mb-3">Reference: <span className="font-mono font-medium">{result.provisional_id}</span></p>
            )}
            {result?.joint_client_tokens?.length > 0 && (
              <div className="mt-4 p-4 bg-teal/10 rounded-md border border-teal/30 text-left">
                <p className="text-sm font-medium text-teal mb-2 flex items-center gap-1.5">
                  <Users className="w-4 h-4" /> Joint Client Onboarding Links
                </p>
                <p className="text-xs text-slate-400 mb-3">
                  Each joint client will receive an email with their onboarding link. You can also share these links directly:
                </p>
                {result.joint_client_tokens.map((jc, i) => (
                  <div key={i} className="mb-2 p-2 bg-navy-card rounded border border-navy-border">
                    <p className="text-sm font-medium text-txt">{jc.name}</p>
                    <p className="text-xs text-slate-500">{jc.email}</p>
                    <p className="text-xs text-teal font-mono mt-1 break-all">
                      {process.env.REACT_APP_BACKEND_URL || ""}/client-onboarding/joint/{jc.token}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <p className="text-slate-400 text-xs mt-4">
              Questions? Contact <a href="mailto:clientcare@bonsai.law" className="text-teal hover:underline">clientcare@bonsai.law</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-navy">
      <img src={KOI_BG_URL} alt="" aria-hidden="true" style={koiStyle} />

      <div className="relative z-10">
        <div className="glass-nav py-4 px-6">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <img src={LOGO_URL} alt="Bonsai Law" className="h-10" />
            <span className="text-xs text-slate-400">Client Onboarding</span>
          </div>
        </div>

        <div className="max-w-3xl mx-auto p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-6 mt-2">
            <StepBadge num="1" label="Terms of Business" active={step === "tob"} done={step === "form"} />
            <div className="h-px flex-1 bg-navy-hover" />
            <StepBadge num="2" label="Your Details" active={step === "form"} done={false} />
          </div>

          {step === "tob" && (
            <div data-testid="tob-step">
              <div className="glass-card overflow-hidden">
                <div className="p-5 border-b border-navy-border">
                  <h1 className="text-xl font-bold text-white">Bonsai Law Ltd — Terms of Business</h1>
                  <p className="text-sm text-slate-500 mt-1">{TOB_VERSION}. Please read all terms carefully before proceeding</p>
                </div>
                <div ref={tobContainerRef} onScroll={handleScroll}
                  className="min-h-[420px] h-[65vh] overflow-y-auto p-5 space-y-6 text-sm text-txt leading-relaxed"
                  data-testid="tob-scroll-container">
                  {TOB_SECTIONS.map((section, i) => (
                    <div key={i}>
                      <h3 className="font-semibold text-white mb-2">{section.title}</h3>
                      <p className="whitespace-pre-line">{section.content}</p>
                    </div>
                  ))}
                  <div className="pt-4 border-t border-navy-border">
                    <p className="text-xs text-slate-400 text-center">{FIRM.name} | Terms of Business | {TOB_VERSION}</p>
                  </div>
                </div>
                <div className="p-5 border-t border-navy-border bg-navy-hover/60 space-y-3">
                  {!hasScrolledToBottom && (
                    <p className="text-xs text-status-amber text-center" data-testid="scroll-prompt">
                      Please scroll to the bottom of the Terms of Business to continue
                    </p>
                  )}
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="tob-accept"
                      checked={tobAccepted}
                      onCheckedChange={handleTobAccepted}
                      disabled={!hasScrolledToBottom}
                      data-testid="tob-checkbox"
                    />
                    <label htmlFor="tob-accept" className={`text-sm leading-snug ${hasScrolledToBottom ? "text-txt" : "text-slate-500"}`}>
                      I confirm that I have read and agree to {FIRM.name}'s Terms of Business ({TOB_VERSION}).
                    </label>
                  </div>
                  <Button
                    onClick={() => setStep("form")}
                    disabled={!hasScrolledToBottom || !tobAccepted}
                    className="w-full h-11 bg-teal hover:bg-teal-hover text-white disabled:opacity-40"
                    data-testid="proceed-to-form-button"
                  >
                    Continue to Onboarding Form
                  </Button>
                </div>
              </div>
            </div>
          )}

          {step === "form" && (
            <div data-testid="onboarding-form-step">
              <div className="glass-card overflow-hidden">
                <div className="p-5 border-b border-navy-border">
                  <h1 className="text-xl font-bold text-white">Client Onboarding Form</h1>
                  <p className="text-sm text-slate-500 mt-1">
                    Please complete as fully as possible. Contact <a href="mailto:clientcare@bonsai.law" className="text-teal hover:underline">clientcare@bonsai.law</a> with questions.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-5">

                  {showMatterTypeSelect && (
                    <div className="space-y-1.5" data-testid="matter-type-fallback-section">
                      <Label className="text-txt font-medium">
                        What type of matter is this? <span className="text-status-red">*</span>
                      </Label>
                      <p className="text-xs text-slate-400">
                        This helps us direct your onboarding correctly. You can select "Other / Not sure" if you're unsure.
                      </p>
                      <select
                        value={matterType}
                        onChange={e => setMatterType(e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-navy-border bg-navy-card text-txt text-sm"
                        data-testid="matter-type-select"
                      >
                        <option value="">Select matter type...</option>
                        {MATTER_TYPE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Entity type selector — highlighted red if submit attempted without selection */}
                  <div className="space-y-2">
                    <Label className="text-txt font-medium">
                      What type of client are you? <span className="text-status-red">*</span>
                    </Label>
                    <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-md transition-all ${
                      entityTypeError ? "outline outline-2 outline-red-500/60 outline-offset-2" : ""
                    }`}>
                      {["individual", "company", "llp", "other"].map(t => (
                        <button key={t} type="button" onClick={() => handleEntityTypeSelect(t)}
                          className={`py-2.5 px-3 rounded-md border-2 text-sm font-medium transition-all capitalize ${
                            entityType === t
                              ? "border-teal bg-teal/10 text-teal"
                              : entityTypeError
                              ? "border-red-500/50 text-txt-muted hover:border-red-400"
                              : "border-navy-border text-txt-muted hover:border-navy-border"
                          }`}
                          data-testid={`entity-type-${t}`}
                        >
                          {t === "llp" ? "LLP" : t}
                        </button>
                      ))}
                    </div>
                    {entityTypeError && (
                      <p className="text-xs text-status-red flex items-center gap-1" data-testid="entity-type-error">
                        <AlertCircle className="w-3 h-3" /> Please select your client type to continue
                      </p>
                    )}
                    {entityType === "other" && (
                      <div className="mt-2 p-3 rounded-md bg-status-amber-dim/30 border border-amber-500/40 text-sm text-status-amber" data-testid="entity-other-warning">
                        This form cannot be completed for this entity type. Please contact{" "}
                        <a href="mailto:clientcare@bonsai.law" className="underline text-teal">clientcare@bonsai.law</a>{" "}
                        so we can assist you directly.
                      </div>
                    )}
                  </div>

                  {entityType === "individual" && (
                    <div className="space-y-4 p-4 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="individual-fields">
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Individual Client Details</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-txt text-sm">Date of Birth <span className="text-status-red">*</span></Label>
                          <Input type="date" max={new Date().toISOString().slice(0, 10)} min="1900-01-01" value={formData.date_of_birth} onChange={e => handleChange("date_of_birth", e.target.value)} className="h-10" data-testid="dob-input" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-txt text-sm">Nationality <span className="text-status-red">*</span></Label>
                          <Input value={formData.nationality} onChange={e => handleChange("nationality", e.target.value)} placeholder="e.g. British" className="h-10" data-testid="nationality-input" />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">Occupation <span className="text-status-red">*</span></Label>
                        <Input value={formData.occupation} onChange={e => handleChange("occupation", e.target.value)} placeholder="e.g. Software Engineer" className="h-10" data-testid="occupation-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-txt text-sm">Are you acting in your own name and on your own behalf? <span className="text-status-red">*</span></Label>
                        <RadioGroup value={formData.acting_for_self} onValueChange={v => handleChange("acting_for_self", v)} data-testid="acting-for-self">
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="yes" id="afs-yes" />
                            <label htmlFor="afs-yes" className="text-sm text-txt-muted">Yes — I am the sole beneficial owner</label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="no" id="afs-no" />
                            <label htmlFor="afs-no" className="text-sm text-txt-muted">No — I am acting on behalf of another person or entity</label>
                          </div>
                        </RadioGroup>
                        {formData.acting_for_self === "no" && (
                          <div className="space-y-1.5 mt-2 p-3 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="acting-for-self-details-section">
                            <Label className="text-txt text-sm">Please give brief details <span className="text-status-red">*</span></Label>
                            <p className="text-xs text-slate-400">Whose behalf are you acting on, your relationship to them, and any written authority you hold (e.g. Power of Attorney, executor of estate, parent of minor, company directorship).</p>
                            <textarea
                              value={formData.acting_for_self_details}
                              onChange={e => handleChange("acting_for_self_details", e.target.value)}
                              placeholder="e.g. Acting for my mother under a registered LPA dated 12 March 2024."
                              className="w-full min-h-[80px] p-2 rounded-md border border-navy-border bg-navy-card text-txt text-sm"
                              data-testid="acting-for-self-details-input"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {showNiNumber && (
                    <div className="space-y-2 p-4 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="ni-number-section">
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">National Insurance Number</Label>
                        <p className="text-xs text-slate-400">
                          If you are buying a property or taking a lease, we need your National Insurance number to complete the Stamp Duty Land Tax (SDLT) return we submit to HMRC on your behalf. If you are not, you can leave this blank.
                        </p>
                        <Input value={formData.national_insurance_number} onChange={e => handleChange("national_insurance_number", e.target.value)} placeholder="e.g. AB 12 34 56 C" className="h-10" data-testid="ni-number-input" />
                      </div>
                    </div>
                  )}

                  {["company", "llp"].includes(entityType) && (
                    <div className="space-y-4 p-4 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="corporate-fields">
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Corporate Client Details</p>
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">{entityType === "llp" ? "LLP" : "Company"} Name <span className="text-status-red">*</span></Label>
                        <Input value={formData.company_name} onChange={e => handleChange("company_name", e.target.value)} placeholder={`${entityType === "llp" ? "LLP" : "Company"} name`} className="h-10" data-testid="company-name-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">Registration Number <span className="text-status-red">*</span></Label>
                        <Input value={formData.company_registration_number} onChange={e => handleChange("company_registration_number", e.target.value)} placeholder="e.g. 12345678" className="h-10" data-testid="company-reg-input" />
                      </div>
                      {/* BM-H3851: recited in the Letter of Authority. */}
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">Registered Office Address <span className="text-status-red">*</span></Label>
                        <Input value={formData.company_registered_office} onChange={e => handleChange("company_registered_office", e.target.value)} placeholder="As shown at Companies House" className="h-10" data-testid="company-registered-office-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">UTR or VAT Number</Label>
                        {/* BM-H3668: say why, on the matters where an SDLT return is filed. */}
                        {SDLT_MATTER_TYPES.includes(matterType) && (
                          <p className="text-xs text-slate-400" data-testid="utr-vat-sdlt-explanation">
                            If the {entityType === "llp" ? "LLP" : "company"} is buying a property or taking a lease, we need its UTR or VAT number to complete the Stamp Duty Land Tax (SDLT) return we submit to HMRC on its behalf.
                          </p>
                        )}
                        <div className="flex gap-2">
                          <select value={formData.utr_vat_type} onChange={e => handleChange("utr_vat_type", e.target.value)} className="h-10 px-3 rounded-md border border-navy-border bg-navy-card text-txt text-sm" data-testid="utr-vat-select">
                            <option value="VAT">VAT</option>
                            <option value="UTR">UTR</option>
                          </select>
                          <Input value={formData.utr_or_vat} onChange={e => handleChange("utr_or_vat", e.target.value)} placeholder={`Enter ${formData.utr_vat_type} number`} className="h-10 flex-1" data-testid="utr-vat-input" />
                        </div>
                      </div>
                      {showCompaniesHouseAuthCode && (
                        <div className="space-y-1.5">
                          <Label className="text-txt text-sm">Companies House Authentication Code</Label>
                          {/* BM-H3490: the firm's wording (Teams, 3 Oct 2026); clients ask what this code is. */}
                          <div className="text-xs text-slate-400 space-y-1.5" data-testid="coho-auth-code-explanation">
                            <p>
                              This is a six-character security code issued by Companies House for the {entityType === "llp" ? "LLP" : "company"}. It is needed to authorise online filings, for example annual confirmation statements, accounts and updates to the {entityType === "llp" ? "LLP's" : "company's"} statutory details.
                            </p>
                            <p>
                              The code is normally sent by post to the {entityType === "llp" ? "LLP's" : "company's"} registered office address. If you have it on file, please forward it to us securely. If it has been lost or you cannot locate it, a replacement can be requested through Companies House WebFiling. The replacement is sent by post, normally to the registered office, and Companies House says this can take up to 10 working days.
                            </p>
                            <p>
                              As the code permits filings to be made for the {entityType === "llp" ? "LLP" : "company"}, please do not send it to unverified third parties.
                            </p>
                          </div>
                          <Input value={formData.companies_house_auth_code} onChange={e => handleChange("companies_house_auth_code", e.target.value)} placeholder="e.g. Ab1 2Cd3" className="h-10" data-testid="coho-auth-code-input" />
                        </div>
                      )}
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">Are you a director/member of the {entityType === "llp" ? "LLP" : "company"}? <span className="text-status-red">*</span></Label>
                        <div className="flex gap-3">
                          {["yes", "no"].map(v => (
                            <button key={v} type="button" onClick={() => handleChange("is_director", v)}
                              className={`px-6 py-2 rounded-md border-2 text-sm font-medium transition-all capitalize ${formData.is_director === v ? "border-teal bg-teal/10 text-teal" : "border-navy-border text-txt-muted"}`}
                              data-testid={`director-${v}`}>{v}</button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">Your capacity <span className="text-status-red">*</span></Label>
                        <select value={formData.capacity} onChange={e => handleChange("capacity", e.target.value)}
                          className="w-full h-10 px-3 rounded-md border border-navy-border bg-navy-card text-txt text-sm" data-testid="capacity-select">
                          <option value="">Select your capacity...</option>
                          <option value="Director">Director</option>
                          <option value="Member">Member</option>
                          <option value="Authorised Signatory">Authorised Signatory</option>
                          <option value="Secretary">Secretary</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-txt text-sm">Are you duly authorised to act on behalf of the {entityType === "llp" ? "LLP" : "company"} in this matter? <span className="text-status-red">*</span></Label>
                        <RadioGroup value={formData.acting_for_entity} onValueChange={v => handleChange("acting_for_entity", v)} data-testid="acting-for-entity">
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="yes" id="afe-yes" />
                            <label htmlFor="afe-yes" className="text-sm text-txt-muted">Yes — I am authorised to instruct solicitors on behalf of the {entityType === "llp" ? "LLP" : "company"}</label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="no" id="afe-no" />
                            <label htmlFor="afe-no" className="text-sm text-txt-muted">No — please contact me to discuss authorisation</label>
                          </div>
                        </RadioGroup>
                      </div>
                      <div className="space-y-2" data-testid="beneficial-owners-section">
                        <Label className="text-txt text-sm">
                          Beneficial owner(s) / Persons with Significant Control (PSCs) <span className="text-status-red">*</span>
                        </Label>
                        <p className="text-xs text-slate-400">
                          As part of our anti-money laundering obligations, we are required to identify the beneficial owners of the {entityType === "llp" ? "LLP" : "company"}. Please list any individual(s) who own or control more than 25% of the {entityType === "llp" ? "LLP" : "company"}, or who otherwise exercise significant control. Add one row per person.
                        </p>
                        <div className="space-y-2">
                          {formData.beneficial_owners.map((bo, i) => (
                            <div key={i} className="flex gap-2 items-start" data-testid={`beneficial-owner-row-${i}`}>
                              <Input
                                value={bo.name}
                                onChange={e => updateBeneficialOwner(i, "name", e.target.value)}
                                placeholder="Full name"
                                className="h-9 text-sm flex-1"
                                disabled={formData.no_individual_pscs}
                                data-testid={`beneficial-owner-name-${i}`}
                              />
                              <Input
                                value={bo.percentage}
                                onChange={e => updateBeneficialOwner(i, "percentage", e.target.value)}
                                placeholder="e.g. 60% shareholder"
                                className="h-9 text-sm flex-1"
                                disabled={formData.no_individual_pscs}
                                data-testid={`beneficial-owner-percentage-${i}`}
                              />
                              <button
                                type="button"
                                onClick={() => removeBeneficialOwner(i)}
                                disabled={formData.no_individual_pscs}
                                className="p-2 text-slate-400 hover:text-status-red disabled:opacity-40"
                                data-testid={`remove-beneficial-owner-${i}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addBeneficialOwner}
                            disabled={formData.no_individual_pscs}
                            className="shrink-0"
                            data-testid="add-beneficial-owner"
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" /> Add beneficial owner
                          </Button>
                        </div>
                        <div className="flex items-start gap-2 pt-1">
                          <Checkbox
                            id="no-pscs"
                            checked={formData.no_individual_pscs}
                            onCheckedChange={toggleNoIndividualPscs}
                            data-testid="no-individual-pscs-checkbox"
                          />
                          <label htmlFor="no-pscs" className="text-xs text-slate-400 leading-snug">
                            No individual PSCs — the {entityType === "llp" ? "LLP" : "company"} has no individuals owning or controlling more than 25% (e.g. publicly listed, fully diffuse ownership).
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* BM-H604: a client form never searches the firm's contacts. The old
                        staff contact search here called a staff-only route, which sent the
                        client to the staff M365 login, and could offer other clients' details. */}
                    <div className="space-y-1.5">
                      <Label className="text-txt text-sm">Your Full Name <span className="text-status-red">*</span></Label>
                      <Input type="text" name="full_name" autoComplete="name" value={formData.full_name} onChange={e => handleChange("full_name", e.target.value)} placeholder="Full name" className="h-10" data-testid="full-name-input" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">Email Address <span className="text-status-red">*</span></Label>
                        <Input type="email" value={formData.email} onChange={e => handleChange("email", e.target.value)} placeholder="you@example.com" className="h-10" data-testid="email-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">Phone Number <span className="text-status-red">*</span></Label>
                        <Input type="tel" value={formData.phone} onChange={e => handleChange("phone", e.target.value)} placeholder="Phone number" className="h-10" data-testid="phone-input" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-txt text-sm">Address <span className="text-status-red">*</span></Label>
                      <Textarea value={formData.address} onChange={e => handleChange("address", e.target.value)} placeholder="Full address" rows={3} className="resize-none" data-testid="address-input" />
                    </div>
                  </div>

                  {/* Stage B3: matter_description requires 20-1000 chars
                      so the FORF prefill cannot inherit a one-liner scope. */}
                  <div className="space-y-1.5">
                    <Label className="text-txt text-sm">
                      Brief description of the matter / service required
                      <span className="text-status-red"> *</span>
                    </Label>
                    <Textarea
                      value={formData.matter_description}
                      onChange={e => handleChange("matter_description", e.target.value)}
                      placeholder="Describe the matter (20-1000 chars). e.g. Purchase of 10 High Street, Maidstone, Kent — first-time buyer, including searches, lender liaison, and exchange/completion handling."
                      rows={3}
                      minLength={20}
                      maxLength={1000}
                      required
                      className="resize-none"
                      data-testid="matter-description-input"
                    />
                    <p className="text-[11px] text-slate-500">
                      {(formData.matter_description || "").length}/1000 chars (min 20)
                    </p>
                  </div>

                  {/* BM-H3851 (Vanessa, 7 Oct 2026): every client is asked whether
                      anyone else will instruct us, so that person can be sent for
                      an identity check at the AML step and named in the Letter of
                      Authority. Payment instructions are off unless ticked. */}
                  <div className="space-y-3 p-4 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="others-instructing-section">
                    <div className="space-y-2">
                      <Label className="text-txt text-sm">Will anyone other than you give us instructions on this matter? <span className="text-status-red">*</span></Label>
                      <p className="text-xs text-slate-400">
                        For example another director, a colleague, a family member or your accountant. If so, we will ask you to sign a Letter of Authority, and we will need to check that person's identity before we take instructions from them.
                      </p>
                      <RadioGroup value={formData.others_instructing} onValueChange={setOthersInstructing} data-testid="others-instructing">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="no" id="oi-no" />
                          <label htmlFor="oi-no" className="text-sm text-txt-muted">No, only I will give instructions</label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="yes" id="oi-yes" />
                          <label htmlFor="oi-yes" className="text-sm text-txt-muted">Yes, someone else will also give instructions</label>
                        </div>
                      </RadioGroup>
                    </div>
                    {formData.others_instructing === "yes" && (
                      <div className="space-y-4">
                        {formData.authorised_instructors.map((p, i) => (
                          <div key={i} className="space-y-3 p-3 rounded-md border border-navy-border bg-navy-card" data-testid={`authorised-person-${i}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-txt">Person {i + 1}</span>
                              <button type="button" onClick={() => removeAuthorisedPerson(i)} className="p-1 text-slate-400 hover:text-status-red" data-testid={`authorised-remove-${i}`}>
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <Label className="text-txt text-sm">Full name <span className="text-status-red">*</span></Label>
                                <Input value={p.full_name} onChange={e => updateAuthorisedPerson(i, "full_name", e.target.value)} className="h-10" data-testid={`authorised-name-${i}`} />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-txt text-sm">Role, or relationship to you <span className="text-status-red">*</span></Label>
                                <Input value={p.role} onChange={e => updateAuthorisedPerson(i, "role", e.target.value)} placeholder="e.g. Finance director, spouse" className="h-10" data-testid={`authorised-role-${i}`} />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-txt text-sm">Email address <span className="text-status-red">*</span></Label>
                                <Input type="email" value={p.email} onChange={e => updateAuthorisedPerson(i, "email", e.target.value)} className="h-10" data-testid={`authorised-email-${i}`} />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-txt text-sm">Mobile number <span className="text-status-red">*</span></Label>
                                <Input type="tel" value={p.mobile} onChange={e => updateAuthorisedPerson(i, "mobile", e.target.value)} className="h-10" data-testid={`authorised-mobile-${i}`} />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-txt text-sm">Date of birth <span className="text-status-red">*</span></Label>
                                <Input type="date" max={new Date().toISOString().slice(0, 10)} min="1900-01-01" value={p.date_of_birth} onChange={e => updateAuthorisedPerson(i, "date_of_birth", e.target.value)} className="h-10" data-testid={`authorised-dob-${i}`} />
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-txt text-sm">What may they instruct us on? <span className="text-status-red">*</span></Label>
                              {AUTHORISED_SCOPE_OPTIONS.map(([code, label]) => (
                                <div key={code} className="flex items-start gap-2">
                                  <Checkbox
                                    id={`authorised-scope-${i}-${code}`}
                                    checked={(p.scope || []).includes(code)}
                                    onCheckedChange={(c) => toggleAuthorisedScope(i, code, !!c)}
                                    data-testid={`authorised-scope-${i}-${code}`}
                                  />
                                  <label htmlFor={`authorised-scope-${i}-${code}`} className="text-xs text-txt-muted leading-snug">{label}</label>
                                </div>
                              ))}
                              <p className="text-[11px] text-slate-500">
                                Even if you tick payment instructions, we will always check any payment instruction or change of bank details with you by telephone before acting on it.
                              </p>
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-txt text-sm">Any limits on what they may do (optional)</Label>
                              <Input value={p.limits} onChange={e => updateAuthorisedPerson(i, "limits", e.target.value)} className="h-10" data-testid={`authorised-limits-${i}`} />
                            </div>
                          </div>
                        ))}
                        {formData.authorised_instructors.length < 3 && (
                          <Button type="button" variant="outline" size="sm" onClick={addAuthorisedPerson} data-testid="authorised-add">
                            <Plus className="w-3.5 h-3.5 mr-1" /> Add another person
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {showEstateAgentSection && (
                    <div className="space-y-4 p-4 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="estate-agent-authority-section">
                      <div>
                        <Label className="text-txt font-medium">Authority to speak to the estate agent</Label>
                        <p className="text-xs text-slate-500 mt-0.5">
                          If you are buying or selling through an estate agent, please tell us how we may communicate with them. If no estate agent is involved, you can skip these questions.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-txt text-sm">
                          Do you authorise us to discuss the progress of your transaction with the estate agent(s) involved?
                        </Label>
                        <RadioGroup value={estateAgentAuthority} onValueChange={setEstateAgentAuthority} data-testid="ea-authority">
                          <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="ea-auth-yes" /><label htmlFor="ea-auth-yes" className="text-sm text-txt-muted">Yes, I authorise this</label></div>
                          <div className="flex items-center gap-2"><RadioGroupItem value="no" id="ea-auth-no" /><label htmlFor="ea-auth-no" className="text-sm text-txt-muted">No, I do not authorise this</label></div>
                        </RadioGroup>
                      </div>
                      {estateAgentAuthority === "yes" && (
                        <div className="space-y-2">
                          <Label className="text-txt text-sm">Do you also authorise us to tell the estate agent(s) that we are waiting for standard client due diligence documents from you?</Label>
                          <RadioGroup value={estateAgentIdDisclosure} onValueChange={setEstateAgentIdDisclosure} data-testid="ea-id-disclosure">
                            <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="ea-id-yes" /><label htmlFor="ea-id-yes" className="text-sm text-txt-muted">Yes, I authorise this</label></div>
                            <div className="flex items-center gap-2"><RadioGroupItem value="no" id="ea-id-no" /><label htmlFor="ea-id-no" className="text-sm text-txt-muted">No, I do not authorise this</label></div>
                          </RadioGroup>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label className="text-txt text-sm">We will not disclose detailed financial information to estate agents without your further instructions. Do you agree?</Label>
                        <RadioGroup value={estateAgentDetailLimit} onValueChange={setEstateAgentDetailLimit} data-testid="ea-detail-limit">
                          <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="ea-detail-yes" /><label htmlFor="ea-detail-yes" className="text-sm text-txt-muted">Yes</label></div>
                          <div className="flex items-center gap-2"><RadioGroupItem value="no" id="ea-detail-no" /><label htmlFor="ea-detail-no" className="text-sm text-txt-muted">No, please contact me to discuss any disclosure first</label></div>
                        </RadioGroup>
                      </div>
                      {estateAgentAuthority === "yes" && (
                        <div className="space-y-1.5">
                          <Label className="text-txt text-sm">Please name the agent(s) we may speak to about your matter (if any):</Label>
                          <Input value={estateAgentNames} onChange={e => setEstateAgentNames(e.target.value)} placeholder="e.g. Smith & Jones Estate Agents" className="h-10" data-testid="ea-agent-names" />
                        </div>
                      )}
                    </div>
                  )}

                  {showEstateAgentSection && (
                    <div className="space-y-2 p-4 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="will-status-section">
                      <Label className="text-txt text-sm">Do you have an up-to-date will?</Label>
                      <RadioGroup value={hasWill} onValueChange={setHasWill} data-testid="has-will">
                        <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="will-yes" /><label htmlFor="will-yes" className="text-sm text-txt-muted">Yes</label></div>
                        <div className="flex items-center gap-2"><RadioGroupItem value="no" id="will-no" /><label htmlFor="will-no" className="text-sm text-txt-muted">No</label></div>
                        <div className="flex items-center gap-2"><RadioGroupItem value="not_sure" id="will-not-sure" /><label htmlFor="will-not-sure" className="text-sm text-txt-muted">Not sure</label></div>
                      </RadioGroup>
                      {(hasWill === "no" || hasWill === "not_sure") && (
                        <p className="text-xs text-slate-500 mt-1" data-testid="will-guide-note">
                          We'll send you a copy of our wills guide along with a copy of your answers.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="space-y-3 p-4 bg-navy/60 rounded-md border border-navy-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-txt font-medium flex items-center gap-1.5"><Users className="w-4 h-4" /> Joint Clients</Label>
                        <p className="text-xs text-slate-500 mt-0.5">If this matter involves additional clients, add them here. Each will receive their own onboarding link.</p>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={addJointClient} className="shrink-0" data-testid="add-joint-client">
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add
                      </Button>
                    </div>
                    {jointClients.map((jc, i) => (
                      <div key={i} className="flex gap-2 items-start flex-wrap sm:flex-nowrap" data-testid={`joint-client-${i}`}>
                        <Input value={jc.name} onChange={e => updateJointClient(i, "name", e.target.value)} placeholder="Full name" className="h-9 text-sm flex-1 min-w-[140px]" data-testid={`joint-name-${i}`} />
                        <Input type="email" value={jc.email} onChange={e => updateJointClient(i, "email", e.target.value)} placeholder="Email address" className="h-9 text-sm flex-1 min-w-[140px]" data-testid={`joint-email-${i}`} />
                        <Input type="tel" value={jc.mobile || ""} onChange={e => updateJointClient(i, "mobile", e.target.value)} placeholder="Mobile (for Thirdfort)" className="h-9 text-sm flex-1 min-w-[140px]" data-testid={`joint-mobile-${i}`} />
                        <button type="button" onClick={() => removeJointClient(i)} className="p-2 text-slate-400 hover:text-status-red" data-testid={`remove-joint-${i}`}><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>

                  <ClientSignatureField
                    mode={sigMode}
                    onModeChange={setSigMode}
                    typedName={typedSignature}
                    onTypedNameChange={setTypedSignature}
                    canvasRef={sigCanvasRef}
                    fullName={formData.full_name}
                    padTestId="signature-pad"
                    clearTestId="clear-signature"
                    testIdPrefix=""
                  />

                  {/* Named validation error summary */}
                  {errors.length > 0 && (
                    <div className="p-3 rounded-md bg-status-red-dim/30 border border-red-800/50" data-testid="form-error">
                      <p className="text-status-red text-sm font-medium mb-1.5 flex items-center gap-1.5">
                        <AlertCircle className="w-4 h-4" />
                        Please complete the following before submitting:
                      </p>
                      <ul className="space-y-0.5 pl-1">
                        {errors.map((err, i) => (
                          <li key={i} className="text-status-red text-sm flex items-start gap-1.5">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-status-red shrink-0" />
                            {err}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setStep("tob")} className="h-11" data-testid="back-to-tob">Back</Button>
                    <Button type="submit" disabled={submitting || entityType === "other"} className="flex-1 h-11 bg-teal hover:bg-teal-hover text-white disabled:opacity-40" data-testid="submit-onboarding">
                      {submitting ? "Submitting..." : "Submit Onboarding Form"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepBadge({ num, label, active, done }) {
  return (
    <div className={`flex items-center gap-2 text-sm font-medium ${active ? "text-teal" : "text-slate-500"}`}>
      <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${active ? "bg-teal text-white" : done ? "bg-status-green-dim/40 text-status-green" : "bg-navy-hover text-slate-500"}`}>
        {done ? "✓" : num}
      </span>
      {label}
    </div>
  );
}
